// React and Redux imports
import React, { useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

// Material-UI imports
import { Box, TableContainer } from '@mui/material'
import { DataGridPro } from '@mui/x-data-grid-pro'
import { styled } from '@mui/system'

// Custom components and helper imports
import { getBulkInvoicePaymentsColumns } from '../helpers/BulkInvoicePaymentHelper'

// Utility and function imports
import { roundTo } from 'utils/data-manipulation/valueFormatter'
import TotalsRow from '../misc/TotalsRow'
import { PaymentSplit } from '../../types'

interface BulkInvoicePaymentTableProps {
  invoices: PaymentSplit[]
}

const GridStyling = styled('div')(() => ({
  '& .MuiDataGrid-root': {
    border: 'none',
    '& .MuiDataGrid-main': {
      border: 'none',
    },
    '& .MuiDataGrid-virtualScroller': {
      border: 'none',
    },
    '& .MuiDataGrid-footerContainer': {
      border: 'none',
    },
    '& .MuiDataGrid-columnHeaders': {
      fontWeight: 'bold',
      borderBottom: '1px solid rgba(224, 224, 224, 1)',
      borderTop: 'none',
      borderLeft: 'none',
      borderRight: 'none',
    },
    '& .MuiDataGrid-cell': {
      borderRight: 'none',
      borderLeft: 'none',
    },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    '& .MuiDataGrid-row': {
      borderTop: 'none',
    },
    '& .MuiDataGrid-row:not(:last-child)': {
      borderBottom: '1px solid rgba(224, 224, 224, 1)',
    },
  },
}))

const BulkInvoicePaymentTable: React.FC<BulkInvoicePaymentTableProps> = ({ invoices }) => {
  const navigate = useNavigate()

  const handlePaymentClick = useCallback(
    (invoice: PaymentSplit) => {
      const invoiceId = invoice.targetInvoiceId
      navigate(`/app/sales/invoices/${invoiceId}`)
    },
    [navigate],
  )

  const columns = getBulkInvoicePaymentsColumns('$', handlePaymentClick)

  const totals = useMemo(() => {
    return invoices.reduce(
      (acc, invoice) => {
        acc.amountDue = roundTo(acc.amountDue + Number(invoice?.amountDueWhenPaid || 0), 2)
        acc.amountPaid = roundTo(acc.amountPaid + Number(invoice.amount || 0), 2)
        acc.adjustment = roundTo(acc.adjustment + Number(invoice?.adjustment || 0), 2)
        acc.remainder = roundTo(
          acc.remainder +
            Number(invoice?.amountDueWhenPaid || 0) -
            (Number(invoice.amount || 0) + Number(invoice?.adjustment || 0)),
          2,
        )
        return acc
      },
      { amountDue: 0, amountPaid: 0, adjustment: 0, remainder: 0 },
    )
  }, [invoices])

  const CustomTotalsRow: React.FC = React.memo(() => {
    return <TotalsRow totals={totals} />
  })

  return (
    <TableContainer component={Box} sx={{ maxHeight: 350, width: '100%' }} data-testid="bulk-invoice-payment-table">
      <Box flexGrow={1} overflow="auto" data-testid="data-grid-container">
        <GridStyling>
          <DataGridPro
            rows={invoices}
            columns={columns as any}
            disableSelectionOnClick
            autoHeight
            hideFooterPagination
            hideFooterSelectedRowCount
            components={{
              Footer: CustomTotalsRow,
            }}
            data-testid="data-grid"
          />
        </GridStyling>
      </Box>
    </TableContainer>
  )
}

export default BulkInvoicePaymentTable
